import { useMemo, useState } from 'react';
import {
  useGetMenuOptions,
  useGetPosMenu,
  useUpdatePosMenu
} from '@/apis/business/setting/integrations/StreamOrderSettings/hook';
import Checkbox from '@/components/Common/Checkbox';
import Input from '@/components/Common/Input';
import {
  Box,
  Checkbox as MuiCheckbox,
  Chip,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { AiFillStar } from 'react-icons/ai';
import { FiDownload } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { useFormContext, useWatch } from 'react-hook-form';

type MenuJsonDrawerState = { id: number; name: string } | null;

function MenuRowsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <Box
          key={i}
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          flexWrap='wrap'
          gap={1}
          py={1.5}
          px={2}
          border='1px solid #ECECEC'
          borderRadius='8px'
        >
          <Box display='flex' alignItems='center' gap={1}>
            <Skeleton variant='rounded' width={140 + (i % 3) * 40} height={22} />
            <Skeleton variant='rounded' width={88} height={24} sx={{ borderRadius: 999 }} />
          </Box>
          <Box display='flex' alignItems='center' gap={1}>
            <Skeleton variant='rounded' width={150} height={22} />
            <Skeleton variant='circular' width={36} height={36} />
          </Box>
        </Box>
      ))}
    </>
  );
}

function JsonBlockSkeleton() {
  const lineWidths = ['92%', '78%', '85%', '70%', '88%', '65%', '90%', '72%', '84%', '68%', '76%', '82%'];
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: '#F9F9F9',
        border: '1px solid #ECECEC'
      }}
    >
      <Stack spacing={1}>
        {lineWidths.map((w, i) => (
          <Skeleton key={i} variant='rounded' height={12} sx={{ width: w, borderRadius: 0.5 }} />
        ))}
      </Stack>
    </Box>
  );
}

export default function AdditionalSettings() {
  const { control } = useFormContext();
  const theme = useTheme();

  const locations = useWatch({ control, name: 'locations' });
  const locationId = locations?.[0]?.id;

  const { data: menusResponse, isLoading: menusLoading } = useGetMenuOptions(locationId);
  const { mutateAsync: updateMenu } = useUpdatePosMenu(locationId);

  const [menuJsonDrawer, setMenuJsonDrawer] = useState<MenuJsonDrawerState>(null);

  const {
    data: menuJsonData,
    isFetching: menuJsonLoading,
    isError: menuJsonError
  } = useGetPosMenu(locationId, menuJsonDrawer?.id);

  const jsonText = useMemo(() => {
    if (menuJsonData === undefined || menuJsonData === null) return '';
    try {
      return JSON.stringify(menuJsonData, null, 2);
    } catch {
      return String(menuJsonData);
    }
  }, [menuJsonData]);

  const part_payment = useWatch({
    control,
    name: 'configuration.payment_setting.part_payment'
  });

  const handleOfferMenuToggle = async (menuId: number, next: boolean) => {
    await updateMenu({ menuId, offer_menu: next });
  };

  const handleDownloadJson = () => {
    if (!menuJsonDrawer || !jsonText) return;
    const safeName =
      menuJsonDrawer.name
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-') || 'menu';
    const blob = new Blob([jsonText], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}-${menuJsonDrawer.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const menus = menusResponse?.data ?? [];

  return (
    <Box mx={1}>
      <Typography fontSize={14} fontWeight={500} mb={1}>
        Delivery Settings
      </Typography>
      <Box display='flex' gap={3} mb={3}>
        <Box p={1} border='1px solid #ECECEC' borderRadius='8px'>
          <Checkbox control={control} name='configuration.fulfilment_types.pickup' label='Pickup' size='small' />
        </Box>

        <Box p={1} border='1px solid #ECECEC' borderRadius='8px'>
          <Checkbox control={control} name='configuration.fulfilment_types.delivery' label='Delivery' size='small' />
        </Box>
      </Box>

      <Typography fontSize={14} fontWeight={500} mb={1}>
        Payment Settings
      </Typography>
      <Stack spacing={1.5} mb={3}>
        <Stack direction='row' width='100%' gap={4}>
          <Box p={2} border='1px solid #ECECEC' borderRadius='8px' width='100%'>
            <Checkbox
              control={control}
              name='configuration.payment_setting.without_payment'
              label={
                <Typography fontWeight={500} fontSize={16}>
                  Without Payment
                </Typography>
              }
            />
            <Typography variant='body2' color='text.secondary' sx={{ ml: 4, mt: -1 }}>
              No payment required for this order
            </Typography>
          </Box>

          <Box p={2} border='1px solid #ECECEC' borderRadius='8px' width='100%'>
            <Checkbox
              control={control}
              name='configuration.payment_setting.pay_now'
              label={
                <Typography fontWeight={500} fontSize={16}>
                  Pay Now
                </Typography>
              }
            />
            <Typography variant='body2' color='text.secondary' sx={{ ml: 4, mt: -1 }}>
              Full payment required upfront
            </Typography>
          </Box>
        </Stack>

        <Box
          p={2}
          border='1px solid #ECECEC'
          borderRadius='8px'
          display='flex'
          flexWrap='wrap'
          alignItems='center'
          justifyContent='space-between'
          gap={2}
        >
          <Box flex={1} minWidth={200}>
            <Checkbox
              control={control}
              name='configuration.payment_setting.part_payment'
              label={
                <Typography fontWeight={500} fontSize={16}>
                  Part Payment
                </Typography>
              }
            />
            <Typography variant='body2' color='text.secondary' sx={{ ml: 4, mt: -1 }}>
              Partial payment now with remaining due later
            </Typography>
          </Box>
          {part_payment && (
            <Box width={{ xs: '100%', sm: 200 }}>
              <Input
                control={control}
                name='configuration.payment_setting.part_value'
                label='Part Value'
                placeholder='e.g. 14'
                fullWidth
                size='small'
                sx={{ margin: 'auto' }}
                InputProps={{
                  endAdornment: <InputAdornment position='end'>%</InputAdornment>
                }}
              />
            </Box>
          )}
        </Box>
      </Stack>

      <Typography fontSize={14} fontWeight={500} mb={1}>
        Menus
      </Typography>
      <Stack spacing={1} mb={3}>
        {!locationId && (
          <Typography variant='body2' color='text.secondary'>
            Select a location on this integration to load menus.
          </Typography>
        )}
        {menusLoading && locationId ? (
          <Stack spacing={1}>
            <MenuRowsSkeleton count={3} />
          </Stack>
        ) : (
          menus.map((menu) => (
            <Box
              key={menu.id}
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              flexWrap='wrap'
              gap={1}
              py={1.5}
              px={2}
              border='1px solid #ECECEC'
              borderRadius='8px'
            >
              <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                <Typography fontSize={16} fontWeight={500}>
                  {menu.name}
                </Typography>
                {menu.offer_menu ? (
                  <Chip
                    size='small'
                    icon={<AiFillStar color='primary.main' size={14} />}
                    label='Offer Menu'
                    variant='outlined'
                    sx={{ fontSize: 12, color: 'primary.main', bgcolor: 'primary.light', border: 0, px: 0.5 }}
                  />
                ) : null}
              </Box>
              <Box display='flex' alignItems='center' gap={{ xs: 0.5, lg: 0 }} flexWrap='nowrap'>
                <FormControlLabel
                  control={
                    <MuiCheckbox
                      size='small'
                      checked={Boolean(menu.offer_menu)}
                      onChange={(_, checked) => handleOfferMenuToggle(menu.id, checked)}
                    />
                  }
                  label={
                    <Typography color='secondary' fontSize={14}>
                      Mark as Offer Menu
                    </Typography>
                  }
                />
                <IconButton
                  aria-label={`View ${menu.name} JSON`}
                  disabled={!locationId}
                  onClick={() => setMenuJsonDrawer({ id: menu.id, name: menu.name })}
                  sx={{ ml: -1.5 }}
                >
                  <MdOutlineRemoveRedEye color={theme.palette.primary.main} size={24} />
                </IconButton>
              </Box>
            </Box>
          ))
        )}
      </Stack>

      <Typography fontSize={14} fontWeight={500} mb={2}>
        Others
      </Typography>
      <Stack spacing={2}>
        <Input
          control={control}
          name='configuration.abbreviation'
          label='Abbreviation'
          placeholder='Enter Abbreviation'
          multiline
          minRows={4}
          fullWidth
        />
        <Input
          control={control}
          name='configuration.prep_time_minutes'
          label='Default Preparation Time(In Minutes)'
          placeholder='e.g. 15'
          fullWidth
          type='number'
          inputProps={{ min: 0 }}
        />
      </Stack>

      <Drawer
        anchor='right'
        open={Boolean(menuJsonDrawer)}
        onClose={() => setMenuJsonDrawer(null)}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.45)' } }
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 520, md: 640 },
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 2,
            borderBottom: '1px solid #ECECEC',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexShrink: 0
          }}
        >
          <Box minWidth={0}>
            <Typography fontWeight={500} fontSize={20}>
              {menuJsonDrawer?.name ?? 'Menu'}
            </Typography>
            <Typography fontSize={14} color='text.secondary'>
              Full menu and configuration in Stream Order JSON format.
            </Typography>
          </Box>
          <Box display='flex' alignItems='center' gap={0.5}>
            <Box border='1px solid #ECECEC' borderRadius='4px'>
              <IconButton
                aria-label='Download JSON'
                disabled={!jsonText || menuJsonLoading}
                onClick={handleDownloadJson}
                size='small'
              >
                <FiDownload size={16} color='#777777' />
              </IconButton>
            </Box>

            <IconButton aria-label='Close' onClick={() => setMenuJsonDrawer(null)} size='small'>
              <IoClose size={24} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {menuJsonLoading ? (
            <JsonBlockSkeleton />
          ) : menuJsonError ? (
            <Typography color='error'>Could not load menu JSON. Please try again.</Typography>
          ) : (
            <Box
              component='pre'
              sx={{
                m: 0,
                p: 2,
                borderRadius: 2,
                bgcolor: '#F9F9F9',
                border: '1px solid #ECECEC',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: 12,
                lineHeight: 1.5,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {jsonText || '{}'}
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
